// One place where the dashboard's Cognito token becomes an Authorization header
// (#129).
//
// Before this, none of the ~20 `/api/` calls sent anything: the login decided
// what this bundle rendered, and the API accepted every request from anyone.
// Now the backend verifies the ID token and requires the `superusers` group for
// writes, so a call without the header is a 401 — the header is not optional
// decoration, it is what makes the request work.
//
// It is a wrapper rather than a change at each call site because a call site
// that forgets it now fails at runtime with a 401, and there is no reason for
// twenty copies of the same three lines to exist and drift.
//
// Deliberately not a throwing helper: everything about the response, including
// a 401, is the caller's to interpret. It already interprets `res.ok`.

import { fetchAuthSession } from 'aws-amplify/auth';

// The ID token, or null when there is no session. `cognito:groups` — which the
// backend reads to decide the tier — is on the ID token, and it is the same
// token resolveAccount() in App.js reads the tier from.
//
// Failure is a null, never a throw: a signed-out visitor has no session, and in
// unit tests Amplify is not configured at all. Neither is an error here, and
// neither should stop the request being made — the tiers below superuser issue
// no /api/ call anyway, and the endpoints that are open answer without a token.
async function idToken({ forceRefresh = false } = {}) {
  try {
    const session = await fetchAuthSession(forceRefresh ? { forceRefresh: true } : undefined);
    return session?.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

function withToken(options, token) {
  if (!token) return options;
  return {
    ...options,
    headers: { ...(options.headers ?? {}), Authorization: `Bearer ${token}` },
  };
}

// Drop-in for `fetch` on any samst.live/api URL. Same arguments, same returned
// promise, one added header.
export async function apiFetch(url, options = {}) {
  const token = await idToken();
  const res = await fetch(url, withToken(options, token));

  // An ID token lasts an hour and this dashboard is left open all day, so an
  // expired one is the normal case, not an exception. Refresh once and retry
  // before letting the caller render a failure: Amplify holds a refresh token
  // and can usually do it without the operator noticing.
  //
  // Retrying a write is safe here precisely because it was rejected — a 401 is
  // returned by the auth middleware before any route runs, so nothing was
  // created, moved or deleted by the attempt.
  if (res.status !== 401 || !token) return res;

  const refreshed = await idToken({ forceRefresh: true });
  // No new token, or the same one back: retrying would send the same rejected
  // credential a second time. Hand the 401 to the caller instead, and let the
  // operator sign in again.
  if (!refreshed || refreshed === token) return res;

  return fetch(url, withToken(options, refreshed));
}

export default apiFetch;
